import { useRef, useState, useEffect } from 'react';
import { caseStudies } from '@/mocks/casesData';

function useVisible(threshold = 0.1) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { visible, ref };
}

export default function KangshifuCase() {
  const data = caseStudies[1];
  const heroRef = useVisible(0.05);
  const challengeRef = useVisible(0.1);
  const solutionRef = useVisible(0.1);
  const resultsRef = useVisible(0.1);

  const phase1Items = [
    '五码关联体系搭建，实现产品全生命周期数字化追踪',
    '门店动销数据实时采集，扫码行为精准归因',
    '费用投放与动销结果强绑定，每一分营销费用有据可查',
  ];

  return (
    <section className="py-16 md:py-24 bg-surface-warm">
      <div className="w-full px-4 md:px-8 lg:px-12">
        {/* Case Header with Banner */}
        <div ref={heroRef.ref} className={`mb-10 md:mb-14 transition-all duration-700 ${heroRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="relative rounded-2xl overflow-hidden h-48 md:h-72 mb-8">
            <img
              src={data.heroImage}
              alt={`${data.brand}案例背景`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-orange-900/80 via-orange-800/50 to-transparent" />
            <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-12">
              <h2 className="text-2xl md:text-4xl font-bold text-white mb-2">{data.brand}</h2>
              <p className="text-sm md:text-lg text-white/80 max-w-2xl leading-relaxed">{data.subtitle}</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 text-sm text-text-muted">
            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-lg">
              <i className="ri-building-line" /> 行业：{data.industry}
            </span>
            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-lg">
              <i className="ri-service-line" /> 服务：{data.service}
            </span>
          </div>
        </div>

        {/* Challenge */}
        <div ref={challengeRef.ref} className={`mb-10 md:mb-14 transition-all duration-500 ${challengeRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <h3 className="text-lg md:text-xl font-bold text-primary mb-4 flex items-center gap-2">
            <span className="w-8 h-8 flex items-center justify-center bg-orange-500 rounded-lg">
              <i className="ri-error-warning-line text-white text-sm" />
            </span>
            核心挑战
          </h3>
          <div className="bg-white rounded-xl p-5 md:p-7 border border-gray-100">
            <p className="text-sm md:text-base text-text-secondary leading-relaxed mb-4">{data.challengeIntro}</p>
            <ul className="space-y-2.5">
              {data.challengePoints.map((item, i) => (
                <li key={i} className="text-sm text-text-secondary flex items-start gap-2.5">
                  <i className="ri-arrow-right-s-line text-orange-500 mt-0.5 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Solution */}
        <div ref={solutionRef.ref} className={`mb-10 md:mb-14 transition-all duration-500 ${solutionRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <h3 className="text-lg md:text-xl font-bold text-primary mb-4 flex items-center gap-2">
            <span className="w-8 h-8 flex items-center justify-center bg-accent rounded-lg">
              <i className="ri-lightbulb-line text-primary text-sm" />
            </span>
            解决方案
          </h3>

          <div className="bg-primary rounded-xl p-5 md:p-7 text-white mb-6">
            <p className="text-sm md:text-base leading-relaxed">
              融慧数科为康师傅饮料搭建<strong className="text-accent">五码合一全链路数字化体系</strong>，将产品内码、外码、箱内码、箱外码、垛码五码深度关联，打通从生产线到消费者的每一个数据节点，构建完整的链路数据闭环：
            </p>
          </div>

          {/* Phase 1 */}
          <div className="bg-white rounded-xl p-5 md:p-7 border border-gray-100">
            <ul className="space-y-3">
              {phase1Items.map((item, i) => (
                <li key={i} className="text-sm text-text-secondary flex items-start gap-2.5">
                  <span className="w-5 h-5 flex items-center justify-center bg-orange-100 rounded-full shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-orange-600">{i + 1}</span>
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Results */}
        <div ref={resultsRef.ref} className={`transition-all duration-500 ${resultsRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <h3 className="text-lg md:text-xl font-bold text-primary mb-4 flex items-center gap-2">
            <span className="w-8 h-8 flex items-center justify-center bg-green-600 rounded-lg">
              <i className="ri-trophy-line text-white text-sm" />
            </span>
            落地成果
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.results.map((result, i) => (
              <div
                key={i}
                className="bg-white rounded-xl p-5 border border-gray-100 hover:border-green-200 transition-all duration-300 flex items-start gap-3"
              >
                <div className="w-6 h-6 flex items-center justify-center bg-green-100 rounded-full shrink-0 mt-0.5">
                  <i className="ri-check-line text-green-600 text-xs" />
                </div>
                <p className="text-sm text-text-secondary leading-relaxed">{result}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}